import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@core/authentication/auth.service';
import { isInspectorUser } from '@core/authentication/auth.utility';
import { TenantService, UserService } from '@core/services';
import { GoogleAnalyticsService } from '@core/services/google-analytics.service';
import { environment } from '@environments/environment';
import { OiqLayoutAccountModel } from '@oiq-layout/layout.models';
import { CookieService } from 'ngx-cookie-service';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  shareReplay,
  skipWhile,
  switchMap,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  private authService = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private title = inject(Title);
  private tenantService = inject(TenantService);
  private cookieService = inject(CookieService);
  private userService = inject(UserService);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);

  private stylesheet: HTMLLinkElement | null = null;
  private previousHref: string;

  public navigationEnd$ = this.router.events.pipe(filter(event => event instanceof NavigationEnd));

  production = environment.production;
  staging = environment.staging;
  dev = environment.development;
  userRoles$ = new BehaviorSubject<string>('');
  isLoggedIn$ = this.authService.isLoggedIn;
  isMimicUser$ = this.authService.isMimicUserLoggedIn;
  account$ = this.authService.cookieChanges$.pipe(
    debounceTime(100),
    map(_cookie => {
      if (!_cookie.id) {
        return {
          name: '',
          initials: '',
          title: '',
        };
      }
      const _initialsParts = (this.authService.getUserName() || '').split(' ');
      const _firstTwo = [_initialsParts[0][0]];
      if (_initialsParts[1] && _initialsParts[1][0]) {
        _firstTwo.push(_initialsParts[1][0]);
      }
      return {
        name: this.authService.getUserName(),
        initials: _firstTwo.join(''),
        title: this.authService.returnRolesArray().join(', '),
      } as OiqLayoutAccountModel;
    })
  );

  isRouterLoading$ = new BehaviorSubject<boolean>(false);

  tenantId$ = new BehaviorSubject<number>(0);
  tenantData$ = this.tenantId$.pipe(
    skipWhile(_tenantId => !_tenantId),
    switchMap(_tenantId => (_tenantId ? this.tenantService.getById(_tenantId) : of({}))),
    catchError(() => of({})),
    shareReplay()
  );

  logo$ = this.authService.cookieChanges$.pipe(
    map(_cookie => (isInspectorUser(this.authService.userRoles) ? null : _cookie?.id || null)),
    distinctUntilChanged(),
    switchMap(_userId => (_userId ? this.userService.getLogo(_userId) : of(null))),
    map((_response: any) => _response?.location ?? null),
    shareReplay(),
    catchError(_error => of(null))
  );

  constructor() {
    this.isRouterLoading$.next(true);
    this.navigationEnd$.subscribe((event: NavigationEnd) => {
      this.isRouterLoading$.next(false);
    });

    this.authService.cookieChanges$.subscribe(() => {
      const _user = this.authService.getUser();
      if (_user && _user.tenantId) {
        this.tenantId$.next(_user && _user.tenantId ? _user.tenantId : null);
      }
    });

    this.isMimicUser$.subscribe(loggedInAs => this.handleRoles(loggedInAs));

    if (this.production) {
      this.googleAnalyticsService.initialize();
      this.title.setTitle('Onyx IQ');
    } else if (this.staging) {
      this.title.setTitle('[STAGING] Onyx IQ');
    } else if (this.dev) {
      this.title.setTitle('[DEV] Onyx IQ');
    } else {
      this.title.setTitle('[DEV/LOCAL] Onyx IQ');
    }

    if (localStorage.getItem('masterUser')) {
      this.authService.loginAsSet(true);
    } else {
      this.authService.loginAsSet(false);
    }
  }

  handleRoles(loggedInAs) {
    if (
      this.cookieService.get('currentUser') &&
      this.authService.getUser() &&
      this.authService.getUser().roles.filter(role => role.name === 'SUPER_ADMIN').length > 0
    ) {
      this.userRoles$.next('admin');
    } else {
      this.userRoles$.next('user');
    }
  }

  private _logoutAsInProgress = false;

  logoutAsUser() {
    if (this._logoutAsInProgress) {
      return;
    }

    this._logoutAsInProgress = true;
    this.authService.logoutAs().subscribe({
      next: (r: any) => {
        this.removeMasterUser();
        const date = new Date(Date.now() + 1800 * 1000);
        const expireDate = date.toUTCString();
        const userCookieObject = {
          name: r.firstName + ' ' + r.lastName,
          email: r.email,
          roles: r.roles,
          id: r.id,
          resources: r.resources,
          tenantId: r.tenantId,
          expiryTime: expireDate,
        };
        document.cookie =
          'currentUser=' +
          JSON.stringify(userCookieObject) +
          '; expires = ' +
          expireDate +
          '; path=/';
        localStorage.setItem('token', r.apiKey);

        if (localStorage.getItem('selectedWhiteLabels')) {
          localStorage.removeItem('selectedWhiteLabels');
        }

        if (localStorage.getItem('redirect')) {
          this.router.navigate(localStorage.getItem('redirect').split('/'));

          localStorage.removeItem('redirect');
        } else {
          this.router.navigate(['/settings/users']);
        }
      },
      error: () => {},
      complete: () => {
        this.authService.handleMimicUser(false);
        this._logoutAsInProgress = false;
      },
    });
  }

  removeMasterUser() {
    localStorage.removeItem('masterUser');
  }

  handleLogout() {
    switch (this.authService.loggedInAsSnapshot) {
      case true:
        this.logoutAsUser();
        break;

      default:
        this.authService.logout().subscribe((r: any) => {
          document.cookie.split(';').forEach(function (c) {
            document.cookie = c
              .replace(/^ +/, '')
              .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
          });
          localStorage.removeItem('token');
          localStorage.removeItem('selectedWhiteLabels');
          localStorage.removeItem('redirect');
          this.router.navigate(['/login']);
        });
        break;
    }
  }

  //TODO: Check to see if we need userRole
  // handleRoles(loggedInAs) {
  //   this.loggedInAs = loggedInAs;
  //   if (this.cookieService.get('currentUser') &&
  //     this.authService.getUser() &&
  //     this.authService.getUser().roles.filter(role => role.name === 'SUPER_ADMIN').length > 0) {
  //     this.userRole = 'admin';
  //   } else {
  //     this.userRole = 'user';
  //   }
  // }

  handleSearch(event: string) {
    this.router.navigate(['/search-results'], {
      queryParams: { keyword: event },
    });
    // if(this.mobileNavOpen) {
    //   this.closeMobileNav.emit(true);
    // }
  }
}
